/**
 * The annotation rows a saved post carries under its header, on every card surface and in the
 * preview popup (SPEC `saved-posts`).
 *
 * Two rows at most: the list and tag chips, then the note below when there is one. The first chip is
 * always `Saved` — the save row itself, the popup's own label for it — because a post filed in no
 * custom list is still *in* the collection, and a chipless row read as "in no list at all". The saved
 * date is not a third column: it hangs off the list chips on hover, where it costs no width.
 *
 * Everything here is a server-built string (§12.5) — `savedInfo` arrives on
 * `FeedCardDTO`/`PostDetailDTO` already spelled, list names included, so nothing is joined or
 * formatted client-side.
 *
 * The whole block is one `<button>`, which does two jobs at once: it opens the Save dialog (the rows
 * *are* what that dialog edits, so clicking them where they are is the shortest path back), and it
 * makes the card's own "plain click opens the preview" check skip it for free — that check already
 * excludes any `button` ancestor.
 */

import { useRef, useState } from 'react';
import type { SavedInfoDTO } from '@lw/shared';
import { HoverTip } from '../../components';

/**
 * One list chip with the saved date on hover. The date belongs to the save, not to the list, so
 * every chip shows the same string — owning the hover state and the anchor here is what keeps the
 * shared `HoverTip` usable inside a `.map()` (the `IconAction` pattern).
 */
function ListChip({ name, tip }: { name: string; tip: string }) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [hovered, setHovered] = useState(false);

  return (
    <>
      <span
        className="saved-chip"
        ref={(node) => {
          anchorRef.current = node;
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {name}
      </span>
      <HoverTip open={hovered} anchorRef={anchorRef} text={tip} />
    </>
  );
}

export type SavedRowsBlockProps = {
  info: SavedInfoDTO;
  onEdit: () => void;
};

export function SavedRowsBlock({ info, onEdit }: SavedRowsBlockProps) {
  const tip = `Saved ${info.savedAtDisplay}`;

  return (
    <button type="button" className="saved-rows" aria-label="Edit save" onClick={onEdit}>
      <span className="saved-rows__chips">
        {/* The save itself, not a list — there is no system list row to come back from the server. */}
        <ListChip name="Saved" tip={tip} key="list-default" />
        {info.lists.map((list) => (
          <ListChip name={list.name} tip={tip} key={`list-${String(list.id)}`} />
        ))}
        {info.tags.map((tag) => (
          <span className="tag-chip" data-color={tag.color} key={`tag-${String(tag.id)}`}>
            {tag.name}
          </span>
        ))}
      </span>
      {info.note === null ? null : <span className="saved-rows__note">{info.note}</span>}
    </button>
  );
}

export default SavedRowsBlock;
